import { zodResolver } from '@hookform/resolvers/zod';
import { SettingUrls } from 'constants/SettingsUrls';
import { SettingKey, WizardStepName } from 'data/ppoints/constants';
import useActivateProviders from 'features/shipping-provider/hooks/useActivateProviders';
import { ShippingProviderCardSimplifiedSelector } from 'features/shipping-provider/selector/simplified/ShippingProviderCardSimplifiedSelector';
import WizardStepItemDone from 'features/wizard/components/WizardStepItemDone';
import useReportStepLoading from 'features/wizard/hooks/useReportStepLoading';
import useWizard from 'features/wizard/hooks/useWizard';
import { WizardStepContentProps } from 'features/wizard/models/WizardStepContentProps';
import useContextualSaveBar from 'hooks/useContextualSaveBar';
import { useCountries } from 'hooks/useCountries';
import useSettings from 'hooks/useSettings';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ServiceProviderStepForm } from '../../models/service-provider-step-form';
import serviceProviderStepSchema from './schema/serviceProviderStepSchema';

const SAVE_BAR_ID = 'wizard-service-provider-savebar';
const CONTRACT_BANNER_PROVIDER_THRESHOLD = 4;

export const ServiceProviderStep: React.FC<WizardStepContentProps> = ({ onLoadingChange }) => {
    const navigate = useNavigate();
    const wizard = useWizard();
    const isDone = wizard.isStepDone(WizardStepName.choose_service_provider);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useReportStepLoading(isSubmitting, onLoadingChange);

    const { t, i18n } = useTranslation();

    const tKey = i18n.t.bind(i18n) as (key: string) => string;

    const form = useForm<ServiceProviderStepForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(serviceProviderStepSchema),
        defaultValues: {
            countryCode: '',
            selectedProviders: [],
        },
    });

    // provider_to_country is keyed by country name, not code, so countries is needed to resolve
    // the selector's country code into the key this setting is actually stored under.
    const { settings: providerToCountry } = useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);
    const { data: countries } = useCountries();

    // Fires on the selector's first auto-resolved country too, not only on a user-driven change —
    // so this also seeds the form with whatever was already saved for that country, if anything.
    const handleCountryCodeChange = useCallback(
        (countryCode: string) => {
            const countryName = countries?.find((country) => country.country_code === countryCode)?.name;
            const selectedProviders = (countryName && providerToCountry?.[countryName]) || [];
            form.reset({ countryCode, selectedProviders });
        },
        [form.reset, countries, providerToCountry]
    );

    const { activateProviders } = useActivateProviders();

    const onSubmit = useCallback(
        async (data: ServiceProviderStepForm) => {
            const previousDefaults = form.formState.defaultValues as ServiceProviderStepForm;

            setIsSubmitting(true);

            if (!form.formState.isDirty) {
                await wizard.handleWizardStepper(WizardStepName.choose_service_provider);
                setIsSubmitting(false);
                return;
            }

            form.reset(data);

            try {
                const activated = await activateProviders(data.countryCode, data.selectedProviders);

                if (!activated) {
                    form.reset(previousDefaults);
                    form.reset(data, { keepDefaultValues: true });
                }
            } finally {
                setIsSubmitting(false);
            }
        },
        [activateProviders, form]
    );

    const { saveBar } = useContextualSaveBar<ServiceProviderStepForm>(form, onSubmit, SAVE_BAR_ID, false);

    useLayoutEffect(() => {
        return () => {
            shopify.saveBar.hide(SAVE_BAR_ID).catch(() => {});
        };
    }, []);

    if (isDone) {
        return (
            <WizardStepItemDone
                message={t('WIZARD.SERVICE_PROVIDER_STEP.DESCRIPTION')}
                action={
                    <s-button onClick={() => navigate(SettingUrls.shippingProviders)}>
                        {t('WIZARD.SERVICE_PROVIDER_STEP.MODIFY_SETTINGS')}
                    </s-button>
                }
            />
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {saveBar}
            <s-text>{t('WIZARD.SERVICE_PROVIDER_STEP.DESCRIPTION')}</s-text>
            <s-divider />
            <Controller
                control={form.control}
                name="selectedProviders"
                render={({ field, fieldState }) => (
                    <>
                        <ShippingProviderCardSimplifiedSelector
                            selectedProviders={field.value}
                            onProviderSelectionChange={field.onChange}
                            onCountryCodeChange={handleCountryCodeChange}
                        />
                        {fieldState.error?.message && <s-text tone="critical">{tKey(fieldState.error.message)}</s-text>}
                        {field.value?.length >= CONTRACT_BANNER_PROVIDER_THRESHOLD && (
                            <s-banner tone="warning" heading={t('WIZARD.SERVICE_PROVIDER_STEP.PLEASE_NOTE')}>
                                {t('WIZARD.SERVICE_PROVIDER_STEP.CONTRACT_REQUIRED_BANNER')}
                            </s-banner>
                        )}
                    </>
                )}
            />
            <div className="flex justify-end pt-1">
                <s-button
                    variant="primary"
                    loading={isSubmitting}
                    onClick={() => {
                        form.handleSubmit(onSubmit)();
                    }}
                >
                    {t('WIZARD.STEP_NEXT_BUTTON')}
                </s-button>
            </div>
        </div>
    );
};
